import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    LinearProgress,
    IconButton,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import ShareIcon from "@mui/icons-material/Share";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import dayjs from "dayjs";
import { useMediaQuery } from "@mui/material";
import { incrementActionCounter } from "../../utils/actionCounters";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const ShareReportDialog = ({ isOpen, onClose, user, policies, isAdmin }) => {
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState("");
    const [branchName, setBranchName] = useState("");
    const [selectedDate, setSelectedDate] = useState(dayjs()); // Date in the picker
    const [reportDate, setReportDate] = useState(dayjs()); // Date currently shown in report
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateChanged, setDateChanged] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const calendarRef = useRef();

    const getISTTime = () => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        return new Date(now.getTime() + istOffset);
    };

    useEffect(() => {
        if (isOpen) {
            const today = dayjs();
            setSelectedDate(today);
            setReportDate(today);
            setShowDatePicker(false);
            setDateChanged(false);
            generateReport(today);
        } else {
            // Reset states when dialog closes
            setLoading(true);
            setProgress(0);
            setMessage("");
            setBranchName("");
            const today = dayjs();
            setSelectedDate(today);
            setReportDate(today);
            setShowDatePicker(false);
            setDateChanged(false);
        }
    }, [isOpen]);

    const closeCalendar = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowDatePicker(false);
            setIsClosing(false);
        }, 200); // Match animation duration
    };

    // Click outside to close calendar
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (calendarRef.current && !calendarRef.current.contains(e.target)) {
                closeCalendar();
            }
        };

        if (showDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDatePicker]);

    const generateReport = async (targetDate = dayjs()) => {
        setLoading(true);
        setProgress(0);
        setDateChanged(false); // Hide Generate button once generating
        setShowDatePicker(false); // Close date picker

        // Progress animation to match UploadPolicyModal feel
        const timer = setInterval(() => {
            setProgress((oldProgress) => {
                if (oldProgress >= 95) {
                    return oldProgress;
                }
                const diff = Math.random() * 8; // Smaller steps for longer duration
                return Math.min(oldProgress + diff, 95);
            });
        }, 300);

        try {
            // 1. Fetch Agent (Branch) Name
            let agentName = ""; // Empty if not found
            if (user?.uid) {
                const agentDoc = await getDoc(doc(db, "agent-details", user.uid));
                if (agentDoc.exists()) {
                    const data = agentDoc.data();
                    // For staff accounts, use address as branch name; for admin, use name
                    agentName = data.address || data.name || data.displayName || "";
                }
            }
            setBranchName(agentName);

            // 2. Calculate Premium, Pending, and Count from passed policies
            // Filter by the target date instead of always using today
            const targetDateStr = targetDate.format("YYYY-MM-DD");

            const targetDatePolicies = (policies || []).filter(p => {
                if (!p.createdAt) return false;
                // Handle various createdAt formats
                let policyDate;
                if (p.createdAt?.toDate) {
                    policyDate = dayjs(p.createdAt.toDate());
                } else {
                    policyDate = dayjs(p.createdAt);
                }
                return policyDate.isValid() && policyDate.format("YYYY-MM-DD") === targetDateStr;
            });

            const dailyPoliciesCount = targetDatePolicies.length;

            const motorPremium = targetDatePolicies
                .filter(p => p.policyCategory === 'motor')
                .reduce((sum, p) => {
                    const payable = Number(p.amountPayable) || (Number(p.premiumAmount) - Number(p.discount || 0)) || 0;
                    return sum + payable;
                }, 0);

            const nonMotorPremium = targetDatePolicies
                .filter(p => p.policyCategory !== 'motor')
                .reduce((sum, p) => {
                    const payable = Number(p.amountPayable) || (Number(p.premiumAmount) - Number(p.discount || 0)) || 0;
                    return sum + payable;
                }, 0);

            const totalPremium = motorPremium + nonMotorPremium;

            // balance: Sum of (amountPayable - paidAmount)
            const pendingBalances = targetDatePolicies.reduce((sum, p) => {
                const payable = Number(p.amountPayable) || (Number(p.premiumAmount) - Number(p.discount || 0)) || 0;
                const paid = Number(p.paidAmount || p.receivedAmount) || 0;
                return sum + Math.max(payable - paid, 0);
            }, 0);

            const totalOD = targetDatePolicies.reduce((sum, p) => {
                const amount = Number(p.odPremium) || 0;
                return sum + amount;
            }, 0);

            const totalTP = targetDatePolicies.reduce((sum, p) => {
                const amount = Number(p.tpPremium) || 0;
                return sum + amount;
            }, 0);

            const displayDateStr = targetDate.format("DD/MM/YYYY");

            const branchLine = (agentName && !isAdmin) ? `Branch: ${agentName}\n` : "";
            const msg = `${branchLine}${displayDateStr}\n* Policies added: ${dailyPoliciesCount}\n* Total OD: ₹${totalOD.toLocaleString('en-IN')}\n* Total TP: ₹${totalTP.toLocaleString('en-IN')}\n* Total Premium Collected: ₹${totalPremium.toLocaleString('en-IN')}\n    • Motor: ₹${motorPremium.toLocaleString('en-IN')}\n    • Non-Motor: ₹${nonMotorPremium.toLocaleString('en-IN')}\n* Pending Balance: ₹${pendingBalances.toLocaleString('en-IN')}`;

            // Finish progress
            setTimeout(() => {
                clearInterval(timer);
                setProgress(100);
                setTimeout(() => {
                    setMessage(msg);
                    setReportDate(targetDate); // Update the report date to the target date
                    setLoading(false);
                }, 400);
            }, 2800);

        } catch (error) {
            clearInterval(timer);
            console.error("Error generating report:", error);
            // Don't close the modal on error, just show empty state
            setMessage("");
            setReportDate(targetDate);
            setLoading(false);
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            const newDate = dayjs(date);
            setSelectedDate(newDate);
            setReportDate(newDate); // Update displayed date immediately
            setDateChanged(true); // Show Generate button
            closeCalendar(); // Close calendar with animation
        }
    };

    const handleGenerateReport = () => {
        generateReport(selectedDate);
    };

    const handleToggleDatePicker = () => {
        if (showDatePicker) {
            closeCalendar(); // Close with animation
        } else {
            setShowDatePicker(true);
        }
    };

    const handleWhatsAppRedirect = async (msg) => {
        if (!msg) return;

        // Increment reminder counter
        if (user?.uid) {
            await incrementActionCounter(user.uid, 'reportsharercounter');
        }

        const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(msg)}`;
        const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(msg)}`;

        // Match Renewals.jsx logic exactly
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = whatsappAppUrl;
        document.body.appendChild(iframe);

        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 100);

        let appOpened = false;
        const handleBlur = () => {
            appOpened = true;
            window.removeEventListener('blur', handleBlur);
        };
        window.addEventListener('blur', handleBlur);

        setTimeout(() => {
            window.removeEventListener('blur', handleBlur);
            if (!appOpened) {
                window.open(webWhatsappUrl, "_blank", "noopener,noreferrer");
            }
        }, 1500); // 1.5s fallback to match Renewals.jsx
    };

    const handleShare = () => {
        handleWhatsAppRedirect(message);
    };

    const lines = message.split('\n');
    const branchLineText = lines.find(l => l.startsWith("Branch:"));
    const dateLineText = branchLineText ? lines[1] : lines[0];
    const statsLines = lines.filter(l => {
        const trimmed = l.trim();
        return trimmed.startsWith("*") || trimmed.startsWith("•");
    });

    const isMobile = useMediaQuery('(max-width:600px)');

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? "12px" : "12px",
                    width: isMobile ? "90%" : "100%",
                    maxWidth: isMobile ? "360px" : "sm",
                    mx: "auto"
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: isMobile ? 1.5 : 2, textAlign: "left !important", position: 'relative' }}>
                <Box
                    sx={{
                        fontFamily: "'Roboto Slab', serif !important",
                        fontWeight: "500 !important",
                        fontSize: isMobile ? "24px !important" : "28px !important",
                        lineHeight: "1.2 !important",
                        letterSpacing: "0.02em !important",
                        color: "#000000 !important",
                        width: 'auto',
                        mb: 1,
                        pr: 4
                    }}
                >
                    Daily Summary Report
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: 'absolute', right: 8, top: isMobile ? 12 : 20 }}
                >
                    <IoClose size={isMobile ? 24 : 28} />
                </IconButton>

                {/* Date Display and Selector Container - Fixed height to prevent dialog jump */}
                <Box sx={{ minHeight: isMobile ? '40px' : '48px', display: 'flex', alignItems: 'center' }}>
                    {!loading && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mt: 0.5,
                            position: 'relative',
                            width: '100%',
                            pr: isMobile ? 1 : 2 // Push button left to align with share button below
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{
                                    fontFamily: 'Roboto, sans-serif !important',
                                    fontSize: isMobile ? '16px !important' : '18px !important',
                                    fontWeight: '400 !important',
                                    color: '#424242 !important'
                                }}>
                                    📅 {reportDate.format('DD MMM YYYY')}
                                </Typography>
                                <Button
                                    onClick={handleToggleDatePicker}
                                    size="small"
                                    sx={{
                                        textTransform: 'none',
                                        fontFamily: 'Roboto, sans-serif !important',
                                        fontSize: '14px !important',
                                        fontWeight: '400 !important',
                                        color: '#0063F7 !important',
                                        minWidth: 'auto',
                                        p: 0.5,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 99, 247, 0.04) !important'
                                        }
                                    }}
                                >
                                    Change
                                </Button>
                            </Box>

                            {/* Generate Report Button - Now in the same row */}
                            {dateChanged && (
                                <Button
                                    onClick={handleGenerateReport}
                                    variant="contained"
                                    sx={{
                                        height: isMobile ? '32px' : '36px',
                                        borderRadius: '6px',
                                        backgroundColor: '#4CAF50 !important',
                                        color: '#FFFFFF !important',
                                        fontFamily: 'Roboto !important',
                                        fontWeight: '500 !important',
                                        fontSize: isMobile ? '13px !important' : '14px !important',
                                        textTransform: 'none',
                                        px: 2,
                                        '&:hover': {
                                            backgroundColor: '#45a049 !important',
                                            boxShadow: 2
                                        }
                                    }}
                                >
                                    Generate Report
                                </Button>
                            )}

                            {/* Date Picker - react-calendar (Absolute positioned overlay) */}
                            {showDatePicker && (
                                <Box
                                    ref={calendarRef}
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        mt: 1,
                                        zIndex: 1400,
                                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        width: isMobile ? '300px' : '340px',
                                        overflow: 'hidden',
                                        padding: '12px',
                                        animation: isClosing ? 'scaleOut 0.2s ease-in' : 'scaleIn 0.2s ease-out',
                                        transformOrigin: 'top left',
                                        '@keyframes scaleIn': {
                                            '0%': {
                                                opacity: 0,
                                                transform: 'scale(0.8) translateY(-10px)'
                                            },
                                            '100%': {
                                                opacity: 1,
                                                transform: 'scale(1) translateY(0)'
                                            }
                                        },
                                        '@keyframes scaleOut': {
                                            '0%': {
                                                opacity: 1,
                                                transform: 'scale(1) translateY(0)'
                                            },
                                            '100%': {
                                                opacity: 0,
                                                transform: 'scale(0.8) translateY(-10px)'
                                            }
                                        },
                                        '& .react-calendar': {
                                            width: '100%',
                                            maxWidth: '100%',
                                            minHeight: '280px',
                                            border: 'none',
                                            fontFamily: 'Roboto, sans-serif',
                                            '&__navigation': {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '0 6px',
                                                margin: '0 0 8px 0',
                                                '&__label': {
                                                    minWidth: '180px',
                                                    textAlign: 'center',
                                                    flexGrow: 0,
                                                    fontWeight: '600 !important',
                                                    margin: 0,
                                                    padding: '8px 0',
                                                    fontSize: '16px',
                                                    lineHeight: '1.4'
                                                },
                                                '&__arrow': {
                                                    minWidth: '32px',
                                                    padding: '8px 2px',
                                                    margin: 0
                                                }
                                            },
                                            '&__month-view__weekdays': {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '0 6px',
                                                marginBottom: '4px',
                                                '&__weekday': {
                                                    width: '36px',
                                                    padding: '4px 0',
                                                    textAlign: 'center',
                                                    fontSize: '12px',
                                                    fontWeight: '600 !important',
                                                    textTransform: 'uppercase',
                                                    color: '#666 !important',
                                                    backgroundColor: 'transparent !important',
                                                    border: 'none !important',
                                                    '& abbr': {
                                                        textDecoration: 'none',
                                                        display: 'inline-block',
                                                        width: '100%',
                                                        textAlign: 'center'
                                                    }
                                                }
                                            },
                                            '&__month-view__days': {
                                                display: 'grid !important',
                                                gridTemplateColumns: 'repeat(7, 1fr)',
                                                gap: '4px',
                                                padding: '0 6px',
                                                '&__day': {
                                                    width: '36px',
                                                    height: '36px',
                                                    margin: '0 auto',
                                                    fontWeight: '600 !important',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px',
                                                    '&--neighboringMonth': {
                                                        opacity: 0.5
                                                    },
                                                    '&--weekend': {
                                                        color: '#212121 !important'
                                                    },
                                                    '&--now': {
                                                        fontWeight: 'bold',
                                                        color: '#004DC2 !important',
                                                        backgroundColor: 'transparent !important',
                                                        '&:enabled:hover': {
                                                            backgroundColor: '#E0EBFF !important'
                                                        }
                                                    }
                                                }
                                            }
                                        },
                                        '& .react-calendar__tile': {
                                            fontFamily: 'Roboto',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            color: '#212121 !important',
                                            transition: 'all 0.2s ease',
                                            border: 'none',
                                            background: 'none',
                                            '&:enabled:hover': {
                                                backgroundColor: '#E0EBFF !important',
                                                color: '#212121 !important',
                                                borderRadius: '50%'
                                            },
                                            '&:disabled': {
                                                color: '#BDBDBD !important',
                                                backgroundColor: 'transparent !important',
                                                cursor: 'not-allowed',
                                                opacity: 0.5
                                            },
                                            '&--active, &--active:enabled, &--active:enabled:hover': {
                                                backgroundColor: '#004DC2 !important',
                                                color: '#fff !important',
                                                borderRadius: '50%',
                                                border: 'none',
                                                boxShadow: 'none',
                                                outline: 'none'
                                            }
                                        },
                                        "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled": {
                                            backgroundColor: '#004DC2 !important',
                                            color: '#fff !important',
                                        },
                                        "& .react-calendar__month-view__days__day--weekend": {
                                            color: '#212121 !important',
                                        },
                                        "& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover": {
                                            backgroundColor: '#E0EBFF !important',
                                        },
                                        "& .react-calendar__navigation button": {
                                            color: '#333 !important',
                                            background: 'none !important',
                                            fontWeight: '600 !important',
                                            border: 'none !important',
                                            outline: 'none !important',
                                            minWidth: '32px',
                                            '&:hover': {
                                                backgroundColor: '#E0EBFF !important',
                                                color: '#333 !important',
                                                borderRadius: '6px'
                                            },
                                            '&:focus': {
                                                backgroundColor: 'transparent !important',
                                                outline: 'none !important',
                                                boxShadow: 'none !important',
                                            },
                                            '&:active': {
                                                backgroundColor: 'transparent !important',
                                            },
                                        },
                                        "& .react-calendar__navigation__label": {
                                            color: '#333 !important',
                                            background: 'transparent !important',
                                            fontWeight: '600 !important',
                                            '&:hover': {
                                                backgroundColor: 'transparent !important',
                                            },
                                            '&:focus': {
                                                backgroundColor: 'transparent !important',
                                                outline: 'none !important',
                                            },
                                        },
                                    }}
                                >
                                    <Calendar
                                        value={selectedDate.toDate()}
                                        onChange={handleDateChange}
                                        maxDate={new Date()}
                                        locale="en-US"
                                        calendarType="gregory"
                                        tileDisabled={({ date }) => date > new Date()}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent sx={{ textAlign: "center", mt: isMobile ? 0 : 1, p: isMobile ? 2 : 3 }}>
                {loading ? (
                    <Box sx={{ py: isMobile ? 4 : 6, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ position: 'relative', width: '100%', maxWidth: "536px", height: "9px", bgcolor: '#E5E7EB', borderRadius: "12px", overflow: 'hidden' }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: "9px",
                                        borderRadius: "12px",
                                        bgcolor: 'transparent',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: '#0063F7'
                                        }
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontFamily: 'Roboto !important', fontSize: '16px !important', fontWeight: "400 !important", color: '#212121 !important', lineHeight: "24px !important" }}>
                                {Math.round(progress)}%
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: 'Roboto !important', fontSize: '22px !important', fontWeight: "400 !important", color: '#212121 !important', lineHeight: "30px !important", mt: 2 }}>
                            Generating Report...
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'left', mt: 1 }}>
                        {!message || statsLines.length === 0 ? (
                            // Empty State
                            <Box
                                sx={{
                                    backgroundColor: "#FAFAFA",
                                    p: 4,
                                    borderRadius: "4px",
                                    border: "2px dashed #ccc",
                                    minHeight: 180,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Roboto, sans-serif !important',
                                        fontSize: isMobile ? '16px !important' : '18px !important',
                                        fontWeight: '400 !important',
                                        color: '#757575 !important'
                                    }}
                                >
                                    No policies found for {reportDate.format('DD MMM YYYY')}.
                                </Typography>
                            </Box>
                        ) : (
                            // Report Content
                            <Box
                                sx={{
                                    backgroundColor: "#FAFAFA",
                                    p: 3,
                                    borderRadius: "4px",
                                    border: "2px dashed #ccc",
                                    fontFamily: "Roboto, sans-serif",
                                    color: "#212121",
                                    minHeight: 180,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    textAlign: 'left'
                                }}
                            >
                                {branchLineText && (
                                    <Box sx={{ fontFamily: 'Roboto, sans-serif !important', fontSize: '18px !important', fontWeight: "400 !important", mb: 0.5 }}>
                                        {branchLineText}
                                    </Box>
                                )}
                                <Box sx={{ fontFamily: 'Roboto, sans-serif !important', fontSize: '14px !important', fontWeight: "400 !important", mb: 1.5, color: '#757575 !important' }}>
                                    {dateLineText}
                                </Box>
                                {statsLines.map((line, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            fontFamily: 'Roboto, sans-serif !important',
                                            fontSize: line.startsWith("    ") ? '16px !important' : '18px !important',
                                            fontWeight: "400 !important",
                                            mb: 0.5,
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {line}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            {!loading && (
                <DialogActions sx={{
                    p: isMobile ? 1.5 : 2,
                    pb: isMobile ? 2 : 3,
                    pr: isMobile ? 2 : 3,
                    gap: 1.5,
                    justifyContent: 'flex-end',
                    flexDirection: 'row'
                }}>
                    {(isAdmin || !isAdmin) && (
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            sx={{
                                width: isMobile ? "130px" : "149px",
                                height: isMobile ? "44px" : "60px",
                                borderRadius: "6px",
                                borderColor: "#B00020 !important",
                                color: "#B00020 !important",
                                fontFamily: "Roboto !important",
                                fontWeight: "400 !important",
                                fontSize: isMobile ? "15px !important" : "20px !important",
                                lineHeight: "24px !important",
                                letterSpacing: "0.01em !important",
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "rgba(176, 0, 32, 0.04) !important",
                                    borderColor: "#B00020 !important",
                                    boxShadow: 6
                                }
                            }}
                        >
                            {isAdmin ? "Close" : "Cancel"}
                        </Button>
                    )}
                    {!isAdmin && (
                        <Button
                            onClick={handleShare}
                            variant="contained"
                            startIcon={<ShareIcon />}
                            sx={{
                                width: isMobile ? "130px" : "149px",
                                height: isMobile ? "44px" : "60px",
                                borderRadius: "6px",
                                backgroundColor: '#0063F7 !important',
                                color: '#FFFFFF !important',
                                fontFamily: "Roboto !important",
                                fontWeight: "400 !important",
                                fontSize: isMobile ? "15px !important" : "20px !important",
                                lineHeight: "24px !important",
                                letterSpacing: "0.01em !important",
                                textTransform: "none",
                                "&:disabled": {
                                    backgroundColor: "#CCCCCC !important",
                                    color: "#666666 !important",
                                },
                                '&:hover': {
                                    backgroundColor: '#0056d6 !important',
                                    boxShadow: 10
                                }
                            }}
                        >
                            Share Report
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ShareReportDialog;
